import type { ReactNode, RefObject } from 'react';
import { Link } from 'react-router-dom';
import { RosterPilotLogo } from '../../assets/RosterPilotLogo';
import './AcceptInvite.css';

/**
 * The chrome every state of the accept-invitation page renders inside, so each state stays down to
 * its own content.
 */

export const InviteShell = ({ children }: { children: ReactNode }) => (
  <div className="accept-invite-screen">
    <main className="accept-invite-container">
      <div className="accept-invite-logo">
        <RosterPilotLogo />
      </div>
      {children}
    </main>
  </div>
);

/**
 * `tabIndex={-1}` on the heading so a terminal state can move focus to it.
 *
 * Each phase of this page swaps the whole screen, so without that the focused control unmounts,
 * focus falls to `<body>`, and a screen reader is told nothing happened. `AccountPausedScreen` does
 * the same thing for the same reason.
 */
export const InviteHeader = ({
  title,
  subtitle,
  headingRef,
}: {
  title: string;
  subtitle?: string;
  headingRef?: RefObject<HTMLHeadingElement | null>;
}) => (
  <div className="accept-invite-header">
    <h1 className="accept-invite-title" ref={headingRef} tabIndex={-1}>
      {title}
    </h1>
    {subtitle && <p className="accept-invite-subtitle">{subtitle}</p>}
  </div>
);

/**
 * Sign-in link carrying the invited address in router state rather than the query string.
 *
 * Same reasoning as the invitation token itself: `index.html` loads Google Fonts on every route, so
 * an address in the URL would be handed to a third party in a `Referer` header.
 */
export const SignInLink = ({
  email,
  children,
}: {
  email?: string;
  children: ReactNode;
}) => (
  <Link
    to="/login"
    state={email ? { email } : undefined}
    className="accept-invite-link"
    data-testid="sign-in-link"
  >
    {children}
  </Link>
);
